/**
 * useLegislatorData Hook
 * 
 * Shared hook for fetching legislator data from different sources.
 * Supports: all legislators, workspace tracked, report endpoints.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  BaseLegislator,
  LegislatorDataSource,
  LegislatorFilterState,
  SortState,
} from './types';

// =============================================================================
// Types
// =============================================================================

interface UseLegislatorDataOptions {
  /** Data source type */
  source: LegislatorDataSource;
  /** Workspace ID (required for tracked/report sources) */
  workspaceId?: string;
  /** Current filter state (for server-side filtering) */
  filters?: LegislatorFilterState;
  /** Current sort state (for server-side sorting) */
  sort?: SortState;
  /** Enable the query */
  enabled?: boolean;
  /** Stale time for caching */
  staleTime?: number;
  /** For public report view */
  isPublicView?: boolean;
}

interface UseLegislatorDataReturn<T extends BaseLegislator> {
  /** Fetched data */
  data: T[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Total count (from pagination) */
  totalCount: number;
  /** Refetch function */
  refetch: () => void;
  /** Query result for advanced usage */
  query: UseQueryResult<T[], Error>;
}

// Workspace mutations return type
interface UseLegislatorMutationsReturn {
  updatePriority: (legislatorId: number, isPriority: boolean) => Promise<void>;
  updatePosition: (legislatorId: number, position: string) => Promise<void>;
  removeTracking: (legislatorId: number) => Promise<void>;
  removeBulkTracking: (legislatorIds: number[]) => Promise<void>;
  isUpdating: boolean;
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * Hook for fetching legislator data from various sources
 */
export function useLegislatorData<T extends BaseLegislator = BaseLegislator>(
  options: UseLegislatorDataOptions
): UseLegislatorDataReturn<T> {
  const {
    source,
    workspaceId,
    filters,
    sort,
    enabled = true,
    staleTime = 30000,
    isPublicView = false,
  } = options;

  // Determine the query key based on source
  const queryKey = getQueryKey(source, workspaceId, filters, sort);

  // Fetch function based on source
  const fetchFn = async (): Promise<T[]> => {
    switch (source) {
      case 'all':
        return fetchAllLegislators<T>(filters, sort);
      case 'tracked':
        if (!workspaceId) throw new Error('workspaceId required for tracked source');
        return fetchTrackedLegislators<T>(workspaceId);
      case 'report':
        if (!workspaceId) throw new Error('workspaceId required for report source');
        return fetchReportLegislators<T>(workspaceId, false, isPublicView);
      case 'report-all':
        if (!workspaceId) throw new Error('workspaceId required for report-all source');
        return fetchReportLegislators<T>(workspaceId, true, isPublicView);
      default:
        throw new Error(`Unknown data source: ${source}`);
    }
  };

  const query = useQuery<T[], Error>(
    queryKey,
    fetchFn,
    {
      enabled: enabled && (source === 'all' || !!workspaceId),
      staleTime,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    totalCount: query.data?.length || 0,
    refetch: query.refetch,
    query,
  };
}

// =============================================================================
// Workspace Mutations Hook
// =============================================================================

/**
 * Hook for workspace-specific legislator mutations
 */
export function useLegislatorMutations(workspaceId: string): UseLegislatorMutationsReturn {
  const queryClient = useQueryClient();
  
  const invalidateQueries = () => {
    queryClient.invalidateQueries(['workspace-tracked-legislators', workspaceId]);
    queryClient.invalidateQueries(['workspace-legislators', workspaceId]);
  };

  const priorityMutation = useMutation(
    async ({ legislatorId, isPriority }: { legislatorId: number; isPriority: boolean }) => {
      const response = await api.put(
        `/workspace-legislator-tracking/workspace/${workspaceId}/legislator/${legislatorId}/priority`,
        { isPriority }
      );
      if (!response.data.success) {
        throw new Error('Failed to update priority status');
      }
      return response.data;
    },
    { onSuccess: invalidateQueries }
  );

  const positionMutation = useMutation(
    async ({ legislatorId, position }: { legislatorId: number; position: string }) => {
      const response = await api.put(
        `/workspace-legislator-tracking/workspace/${workspaceId}/legislator/${legislatorId}/position`,
        { position }
      );
      if (!response.data.success) {
        throw new Error('Failed to update position');
      }
      return response.data;
    },
    { onSuccess: invalidateQueries }
  );

  const removeMutation = useMutation(
    async (legislatorId: number) => {
      const response = await api.delete('/workspace-legislator-tracking', {
        data: { legislatorId, workspaceIds: [workspaceId] }
      });
      if (!response.data.success) {
        throw new Error('Failed to remove legislator tracking');
      }
      return response.data;
    },
    { onSuccess: invalidateQueries }
  );

  const removeBulkMutation = useMutation(
    async (legislatorIds: number[]) => {
      const response = await api.post('/workspace-legislator-tracking/bulk', {
        legislatorIds,
        workspaceIds: [workspaceId],
        action: 'remove',
      });
      if (!response.data.success) {
        throw new Error('Failed to remove legislator tracking');
      }
      return response.data;
    },
    { onSuccess: invalidateQueries }
  );

  return {
    updatePriority: async (legislatorId: number, isPriority: boolean) => {
      await priorityMutation.mutateAsync({ legislatorId, isPriority });
    },
    updatePosition: async (legislatorId: number, position: string) => {
      await positionMutation.mutateAsync({ legislatorId, position });
    },
    removeTracking: async (legislatorId: number) => {
      await removeMutation.mutateAsync(legislatorId);
    },
    removeBulkTracking: async (legislatorIds: number[]) => {
      await removeBulkMutation.mutateAsync(legislatorIds);
    },
    isUpdating: priorityMutation.isLoading || positionMutation.isLoading || removeMutation.isLoading || removeBulkMutation.isLoading,
  };
}

// =============================================================================
// Tracked Legislators Hook (for bookmark status)
// =============================================================================

/**
 * Hook for fetching tracked legislator IDs (for bookmark status on main page)
 */
export function useTrackedLegislatorIds(): {
  trackedIds: Set<number>;
  isLoading: boolean;
  refetch: () => void;
} {
  const query = useQuery(
    ['tracked-legislators'],
    async () => {
      try {
        const response = await api.get('/workspace-legislator-tracking');
        return response.data.data.legislatorIds || [];
      } catch (error) {
        console.error('Error fetching tracked legislators:', error);
        return [];
      }
    },
    { staleTime: 30000 }
  );

  return {
    trackedIds: new Set(query.data || []),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

// =============================================================================
// Normalization Helper (Safety Net)
// =============================================================================

/**
 * Normalizes legislator data to camelCase format.
 * This is a safety net to ensure consistent property names regardless of backend response.
 * The primary source of truth is camelCase (matching workspace-legislator-tracking endpoint).
 */
function normalizeLegislator<T extends BaseLegislator>(legislator: Record<string, unknown>): T {
  // Build base legislator with normalized camelCase fields
  const normalized: Record<string, unknown> = {
    // Core identification - prefer camelCase, fallback to snake_case
    id: legislator.id || legislator.legislator_id,
    
    // Name fields
    fullName: legislator.fullName || legislator.full_name || '',
    firstName: legislator.firstName || legislator.first_name || '',
    lastName: legislator.lastName || legislator.last_name || '',
    
    // Basic info
    chamber: legislator.chamber || '',
    district: legislator.district || '',
    partyAffiliation: legislator.partyAffiliation || legislator.party_affiliation || '',
    sessionYear: legislator.sessionYear || legislator.session_year || '',
    sessionKey: legislator.sessionKey || legislator.session_key || undefined,
    
    // Contact
    email: legislator.email || '',
    workPhone: legislator.workPhone || legislator.work_phone || '',
    
    // Status and metadata
    status: legislator.status || '',
    committees: legislator.committees || '',
    
    // Counts
    sponsoredBillsCount: parseInt(String(legislator.sponsoredBillsCount || legislator.sponsored_bills_count || 0), 10),
    committeeCount: parseInt(String(legislator.committeeCount || legislator.committee_count || 0), 10),

    // Photo
    photoUrl: legislator.photoUrl || legislator.photo_url || null,
  };
  
  // Add workspace-specific fields only if present in input (for TrackedLegislator)
  if (legislator.isPriority !== undefined || legislator.is_priority !== undefined) {
    normalized.isPriority = legislator.isPriority || legislator.is_priority || false;
  }
  // Incoming members-elect (ELECTION_OUTCOMES_PRD §5.14): the workspace list sends
  // the roster status separately, since its own `status` is the tracking state.
  if (legislator.rosterStatus !== undefined || legislator.roster_status !== undefined) {
    normalized.rosterStatus = legislator.rosterStatus ?? legislator.roster_status ?? null;
    normalized.takesOffice = legislator.takesOffice ?? legislator.takes_office ?? null;
  }
  if (legislator.position !== undefined) {
    normalized.position = legislator.position;
  }
  if (legislator.trackingNotes !== undefined || legislator.tracking_notes !== undefined) {
    normalized.trackingNotes = legislator.trackingNotes || legislator.tracking_notes || '';
  }
  
  return normalized as T;
}

/**
 * Normalizes an array of legislators to consistent camelCase format
 */
function normalizeLegislators<T extends BaseLegislator>(legislators: Record<string, unknown>[]): T[] {
  return legislators.map(leg => normalizeLegislator<T>(leg));
}

// =============================================================================
// Fetch Functions
// =============================================================================

async function fetchAllLegislators<T extends BaseLegislator>(
  filters?: LegislatorFilterState,
  sort?: SortState
): Promise<T[]> {
  const params = new URLSearchParams();
  
  if (sort) {
    params.append('sortBy', sort.field);
    params.append('sortDirection', sort.direction);
  }
  
  if (filters) {
    if (filters.searchQuery) params.append('search', filters.searchQuery);
    if (filters.chamber && filters.chamber !== 'All') params.append('chamber', filters.chamber);
    if (filters.committee && filters.committee !== 'All committees') params.append('committee', filters.committee);
    if (filters.party && filters.party !== 'All parties') params.append('party', filters.party);
    if (filters.district && filters.district !== 'All districts') params.append('district', filters.district);
    if (filters.sessionYear) params.append('session_year', filters.sessionYear);
  }

  const url = `/legislators${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  
  if (response.data.success) {
    // Normalize to ensure consistent camelCase format
    return normalizeLegislators<T>(response.data.data.legislators);
  }
  
  throw new Error('Failed to fetch legislators');
}

async function fetchTrackedLegislators<T extends BaseLegislator>(
  workspaceId: string
): Promise<T[]> {
  const response = await api.get(`/workspace-legislator-tracking/workspace/${workspaceId}`);
  
  if (response.data.success) {
    // Normalize to ensure consistent camelCase format
    return normalizeLegislators<T>(response.data.data.legislators);
  }
  
  throw new Error('Failed to fetch tracked legislators');
}

async function fetchReportLegislators<T extends BaseLegislator>(
  workspaceId: string,
  fetchAll: boolean,
  isPublicView: boolean
): Promise<T[]> {
  const basePath = isPublicView ? '/public/workspace' : '/workspace-reports';
  const endpoint = fetchAll
    ? `${basePath}/${workspaceId}/legislators/all`
    : `${basePath}/${workspaceId}/legislators`;
    
  const response = await api.get(endpoint);
  
  if (response.data.success) {
    // Normalize to ensure consistent camelCase format
    return normalizeLegislators<T>(response.data.data);
  }
  
  throw new Error('Failed to fetch legislators');
}

// =============================================================================
// Utility Functions
// =============================================================================

function getQueryKey(
  source: LegislatorDataSource,
  workspaceId?: string,
  filters?: LegislatorFilterState,
  sort?: SortState
): (string | object | undefined)[] {
  switch (source) {
    case 'all':
      return ['legislators', filters, sort];
    case 'tracked':
      return ['workspace-tracked-legislators', workspaceId];
    case 'report':
      return ['workspace-legislators', workspaceId, 'my'];
    case 'report-all':
      return ['workspace-legislators', workspaceId, 'all'];
    default:
      return ['legislators'];
  }
}

export default useLegislatorData;
